import { httpClient } from "@reframe-mobile/api/http-client";
import type {
  User,
  RegisterDto,
  LoginDto,
  LoginResponse,
  PatchUserDto,
  SaveOnboardingDto,
  AppLockDto,
  VerifyPinDto,
  ChatSession,
  ChatMessage,
  CreateChatSessionDto,
  UpdateChatSessionDto,
  SendMessageDto,
  MoodRecord,
  CreateMoodRecordDto,
  UpdateMoodRecordDto,
  MoodStats,
  MoodStatsQuery,
  ThoughtJournal,
  CreateThoughtJournalDto,
  UpdateThoughtJournalDto,
  Journal,
  CreateJournalDto,
  UpdateJournalDto,
  CopingTool,
  CopingToolUsage,
  CreateCopingToolUsageDto,
  UserMission,
  CreateMissionDto,
  UpdateMissionDto,
  EducationContent,
  ProgressSummary,
  UserBadge,
  UserStreak,
  CreatePushSubscriptionDto,
  PaginatedResponse,
  PaginationParams,
} from "@repo/reframe-interfaces";

const API_PREFIX = "/api/v1";

export const API = {
  auth: {
    register: (data: RegisterDto) =>
      httpClient.post<LoginResponse>(`${API_PREFIX}/auth/register`, data),
    login: (data: LoginDto) =>
      httpClient.post<LoginResponse>(`${API_PREFIX}/auth/login`, data),
    logout: () => httpClient.post(`${API_PREFIX}/auth/logout`),
    me: () => httpClient.get<User>(`${API_PREFIX}/auth/me`),
    googleUrl: `${API_PREFIX}/auth/google`,
  },

  user: {
    update: (data: PatchUserDto) =>
      httpClient.patch<User>(`${API_PREFIX}/users/me`, data),
    saveOnboarding: (data: SaveOnboardingDto) =>
      httpClient.post<User>(`${API_PREFIX}/users/me/onboarding`, data),
    setAppLock: (data: AppLockDto) =>
      httpClient.post(`${API_PREFIX}/users/me/app-lock`, data),
    verifyPin: (data: VerifyPinDto) =>
      httpClient.post(`${API_PREFIX}/users/me/verify-pin`, data),
  },

  chat: {
    getSessions: (params?: PaginationParams) =>
      httpClient.get<PaginatedResponse<ChatSession>>(
        `${API_PREFIX}/chat/sessions`,
        { params },
      ),
    getSession: (id: string) =>
      httpClient.get<ChatSession>(`${API_PREFIX}/chat/sessions/${id}`),
    createSession: (data?: CreateChatSessionDto) =>
      httpClient.post<ChatSession>(`${API_PREFIX}/chat/sessions`, data),
    updateSession: (id: string, data: UpdateChatSessionDto) =>
      httpClient.patch<ChatSession>(
        `${API_PREFIX}/chat/sessions/${id}`,
        data,
      ),
    deleteSession: (id: string) =>
      httpClient.delete(`${API_PREFIX}/chat/sessions/${id}`),
    getMessages: (sessionId: string, params?: PaginationParams) =>
      httpClient.get<PaginatedResponse<ChatMessage>>(
        `${API_PREFIX}/chat/sessions/${sessionId}/messages`,
        { params },
      ),
    // SSE streaming endpoint — handled manually via EventSource
    sendMessageUrl: (sessionId: string) =>
      `${API_PREFIX}/chat/sessions/${sessionId}/messages`,
    sendMessage: (sessionId: string, data: SendMessageDto) =>
      httpClient.post<ChatMessage>(
        `${API_PREFIX}/chat/sessions/${sessionId}/messages`,
        data,
      ),
  },

  moodRecord: {
    list: (params?: PaginationParams) =>
      httpClient.get<PaginatedResponse<MoodRecord>>(
        `${API_PREFIX}/mood-records`,
        { params },
      ),
    get: (id: string) =>
      httpClient.get<MoodRecord>(`${API_PREFIX}/mood-records/${id}`),
    create: (data: CreateMoodRecordDto) =>
      httpClient.post<MoodRecord>(`${API_PREFIX}/mood-records`, data),
    update: (id: string, data: UpdateMoodRecordDto) =>
      httpClient.patch<MoodRecord>(`${API_PREFIX}/mood-records/${id}`, data),
    delete: (id: string) =>
      httpClient.delete(`${API_PREFIX}/mood-records/${id}`),
    stats: (params: MoodStatsQuery) =>
      httpClient.get<MoodStats>(`${API_PREFIX}/mood-records/stats`, {
        params,
      }),
  },

  thoughtJournal: {
    list: (params?: PaginationParams) =>
      httpClient.get<PaginatedResponse<ThoughtJournal>>(
        `${API_PREFIX}/thought-journals`,
        { params },
      ),
    get: (id: string) =>
      httpClient.get<ThoughtJournal>(`${API_PREFIX}/thought-journals/${id}`),
    create: (data: CreateThoughtJournalDto) =>
      httpClient.post<ThoughtJournal>(
        `${API_PREFIX}/thought-journals`,
        data,
      ),
    update: (id: string, data: UpdateThoughtJournalDto) =>
      httpClient.patch<ThoughtJournal>(
        `${API_PREFIX}/thought-journals/${id}`,
        data,
      ),
    delete: (id: string) =>
      httpClient.delete(`${API_PREFIX}/thought-journals/${id}`),
  },

  journal: {
    list: (params?: PaginationParams & { type?: string }) =>
      httpClient.get<PaginatedResponse<Journal>>(`${API_PREFIX}/journals`, {
        params,
      }),
    get: (id: string) =>
      httpClient.get<Journal>(`${API_PREFIX}/journals/${id}`),
    create: (data: CreateJournalDto) =>
      httpClient.post<Journal>(`${API_PREFIX}/journals`, data),
    update: (id: string, data: UpdateJournalDto) =>
      httpClient.patch<Journal>(`${API_PREFIX}/journals/${id}`, data),
    delete: (id: string) =>
      httpClient.delete(`${API_PREFIX}/journals/${id}`),
  },

  copingTool: {
    list: () =>
      httpClient.get<PaginatedResponse<CopingTool>>(`${API_PREFIX}/coping-tools`),
    get: (id: number) =>
      httpClient.get<CopingTool>(`${API_PREFIX}/coping-tools/${id}`),
    recordUsage: (data: CreateCopingToolUsageDto) =>
      httpClient.post<CopingToolUsage>(
        `${API_PREFIX}/coping-tools/usage`,
        data,
      ),
    usageHistory: (params?: PaginationParams) =>
      httpClient.get<PaginatedResponse<CopingToolUsage>>(
        `${API_PREFIX}/coping-tools/usage`,
        { params },
      ),
  },

  mission: {
    list: (params?: PaginationParams & { date?: string }) =>
      httpClient.get<PaginatedResponse<UserMission>>(
        `${API_PREFIX}/missions`,
        { params },
      ),
    get: (id: string) =>
      httpClient.get<UserMission>(`${API_PREFIX}/missions/${id}`),
    create: (data: CreateMissionDto) =>
      httpClient.post<UserMission>(`${API_PREFIX}/missions`, data),
    update: (id: string, data: UpdateMissionDto) =>
      httpClient.patch<UserMission>(`${API_PREFIX}/missions/${id}`, data),
    delete: (id: string) =>
      httpClient.delete(`${API_PREFIX}/missions/${id}`),
    generateDaily: () =>
      httpClient.post<UserMission[]>(`${API_PREFIX}/missions/generate`),
  },

  education: {
    list: (params?: PaginationParams & { category?: string }) =>
      httpClient.get<PaginatedResponse<EducationContent>>(
        `${API_PREFIX}/education`,
        { params },
      ),
    get: (id: number) =>
      httpClient.get<EducationContent>(`${API_PREFIX}/education/${id}`),
  },

  progress: {
    summary: (params?: { period?: string }) =>
      httpClient.get<ProgressSummary>(`${API_PREFIX}/progress/summary`, {
        params,
      }),
    badges: () =>
      httpClient.get<UserBadge[]>(`${API_PREFIX}/progress/badges`),
    streak: () =>
      httpClient.get<UserStreak>(`${API_PREFIX}/progress/streak`),
  },

  push: {
    subscribe: (data: CreatePushSubscriptionDto) =>
      httpClient.post(`${API_PREFIX}/push/subscribe`, data),
    unsubscribe: (data: { endpoint: string }) =>
      httpClient.post(`${API_PREFIX}/push/unsubscribe`, data),
  },
};
